// Общие типы и сопоставления для импорта игры из реплея SC2
// (страница «Игры» и «Менеджер матча»).

export interface ReplayPrefillPlayer {
    name: string | null;
    race: string | null;
    result: string | null;
    teamId: number | null;
    toon: string | null;
    /** Номер выбывания (1 = выбыл первым), null — дожил до конца. */
    eliminatedOrder?: number | null;
    /** Общий счёт из реплея: добыто + уничтожено + потеряно + живо (как на экране результатов). */
    score?: number | null;
    /** Оценка добытых ресурсов (минералы + газ). */
    gathered?: number | null;
    /** Стоимость уничтоженного у противников. */
    killed?: number | null;
    /** Стоимость потерянного. */
    lost?: number | null;
    /** Стоимость живого на конец игры (армия + экономика + технологии). */
    army?: number | null;
    /** Секунды до выбывания (дожившие — до конца игры). */
    survivalSeconds?: number | null;
}

export interface ReplayPrefill {
    replayId: string;
    /** Игра, уже добавленная из этого реплея (сервер ищет по id реплея). */
    duplicate?: { game_id: number; played_at: string | null } | null;
    patchVersion: string;
    build: number | null;
    durationSeconds: number;
    playedAt: string | null;
    playedAtMs: number | null;
    gameType: string | null;
    mapTitle: string | null;
    /** Карта, найденная сервером по названию из реплея (основному или альтернативному). */
    map?: { id: number; name: string; alt_name?: string | null } | null;
    replayType: string | null;
    /** Эвристика «реплей полный»: доигран матч и статистика не обрывается. */
    completeness?: {
        complete: boolean;
        reasons: Array<'no_winner' | 'truncated_data'>;
        /** На сколько секунд эко-данные короче длительности игры (null — неизвестно). */
        endGapSeconds: number | null;
    } | null;
    players: ReplayPrefillPlayer[];
    /** Наблюдатели в лобби: в игре не участвовали, но могут быть ведущими. */
    observers?: string[];
    /** Battle.net id модов/карт, чьи настройки лобби есть в реплее (по ним узнаём мод). */
    modNamespaces?: number[];
    /** Выбранные в лобби опции мода (названия из файла мода; пусто, если файл не скачался). */
    lobbySettings?: LobbySetting[];
    /** Карты нет на сайте: данные для формы «новая карта» из заголовка карты в Аркаде. */
    arcadeMap?: ReplayArcadeMap | null;
}

export interface LobbySetting {
    name: string;
    value: string;
}

/** «Game type: KotH · Bounty: 20» — для формы и окна игры. */
export function formatLobbySettings(list: LobbySetting[]): string {
    return list.map((s) => `${s.name}: ${s.value}`).join(' · ');
}

export interface ReplayArcadeMap {
    name: string;
    altName: string | null;
    playerCount: number | null;
    author: string | null;
    link: string | null;
    /** Картинка в депо Blizzard: её отдаёт `GET /api/games/replay-map-image`. */
    image: { region: string; hash: string } | null;
}

/** Реплей отдаёт название расы на языке игры — понимаем латиницу, кириллицу и буквы. */
export function normalizeReplayRace(race: string | null | undefined): 'T' | 'Z' | 'P' | 'R' {
    const s = (race ?? '').trim().toLowerCase();
    if (!s) return 'T';
    if (s.startsWith('z') || s.startsWith('зерг')) return 'Z';
    if (s.startsWith('p') || s.startsWith('протосс')) return 'P';
    if (s.startsWith('r') || s.startsWith('случайн') || s.startsWith('рандом')) return 'R';
    if (s.startsWith('t') || s.startsWith('терр')) return 'T';
    return 'T';
}

/**
 * Убирает клан-тег из ника SC2: `<FFA> Name`, `[FFA] Name`, `{FFA} Name`, `|FFA| Name`.
 * Если после снятия тега ничего не осталось — возвращает исходное имя.
 */
export function stripClanTag(name: string): string {
    let s = name.trim();
    let prev = '';
    while (s && prev !== s) {
        prev = s;
        s = s.replace(/^(<[^<>]{1,24}>|\[[^\[\]]{1,24}\]|\{[^{}]{1,24}\}|\|[^|]{1,24}\|)\s*/, '').trim();
    }
    return s || name.trim();
}

/** Ник в едином виде: без лишних пробелов и регистра. */
export function normalizeNick(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Варианты ника для сопоставления: сам ник и он же без клан-тега.
 * `aka` может содержать несколько ников через запятую/точку с запятой —
 * каждый из них тоже подходит.
 */
export function nickCandidates(value: string | null | undefined): string[] {
    const raw = (value ?? '').trim();
    if (!raw) return [];

    const parts = [raw, ...raw.split(/[,;]+/).map((s) => s.trim()).filter(Boolean)];
    const out = new Set<string>();
    for (const part of parts) {
        out.add(normalizeNick(part));
        out.add(normalizeNick(stripClanTag(part)));
    }
    return [...out].filter(Boolean);
}

/** Ищет игрока из реплея в справочнике по нику/aka (с учётом клан-тегов). */
export function matchReplayPlayer<T extends { name: string; aka?: string | null }>(
    players: readonly T[],
    name: string | null | undefined
): T | undefined {
    if (!name) return undefined;
    const targets = new Set(nickCandidates(name));
    if (targets.size === 0) return undefined;

    return players.find((p) =>
        nickCandidates(p.name).some((n) => targets.has(n))
        || nickCandidates(p.aka).some((n) => targets.has(n))
    );
}

/** Название карты в едином виде (без расширения `.sc2map`). */
function normalizeMapName(name: string): string {
    return name.trim().toLowerCase().replace(/\s+/g, ' ').replace(/\.sc2map$/i, '');
}

/** Находит неудалённую карту по названию (name или alt_name), нормализованно. */
export function findReplayMap<T extends { name: string; alt_name?: string | null; deleted_at?: string | null }>(
    maps: readonly T[],
    title: string | null | undefined
): T | undefined {
    if (!title) return undefined;
    const target = normalizeMapName(title);
    return maps.find(
        (m) =>
            !m.deleted_at &&
            (normalizeMapName(m.name) === target ||
                (m.alt_name ? normalizeMapName(m.alt_name) === target : false))
    );
}
